import { captureViewportSnapshot } from 'common/helpers/domSnapshot'
import { aiApi, UserViewCaptureUpload } from 'services/ai'

// Marks the element holding the app's content (interfaces, code, managers): an
// agent's capture covers it and leaves out the chat panel and the top bar.
export const AGENT_CAPTURE_ROOT_CLASS = 'pp-agent-capture-root'

// Long edge the models are given; larger images are downscaled by them anyway.
const MAX_EDGE_PX = 1568
// An interface the agent just edited may still be reloading its iframe.
const IFRAME_ACK_TIMEOUT_MS = 8000

export type AgentCaptureStatus = UserViewCaptureUpload['status']

const cropToElement = (canvas: HTMLCanvasElement, element: Element): HTMLCanvasElement => {
  const scale = canvas.width / window.innerWidth
  const rect = element.getBoundingClientRect()
  const left = Math.max(0, rect.left)
  const top = Math.max(0, rect.top)
  const width = Math.min(window.innerWidth, rect.right) - left
  const height = Math.min(window.innerHeight, rect.bottom) - top
  if (width <= 0 || height <= 0) return canvas
  const cropped = document.createElement('canvas')
  cropped.width = Math.round(width * scale)
  cropped.height = Math.round(height * scale)
  cropped
    .getContext('2d')
    ?.drawImage(
      canvas,
      Math.round(left * scale),
      Math.round(top * scale),
      cropped.width,
      cropped.height,
      0,
      0,
      cropped.width,
      cropped.height
    )
  return cropped
}

const fitWithin = (canvas: HTMLCanvasElement, maxEdge: number): HTMLCanvasElement => {
  const factor = maxEdge / Math.max(canvas.width, canvas.height)
  if (factor >= 1) return canvas
  const resized = document.createElement('canvas')
  resized.width = Math.round(canvas.width * factor)
  resized.height = Math.round(canvas.height * factor)
  const ctx = resized.getContext('2d')
  if (!ctx) return canvas
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(canvas, 0, 0, resized.width, resized.height)
  return resized
}

const toPng = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('Could not encode the capture'))),
      'image/png'
    )
  })

const pageBackground = (): string => {
  const color = getComputedStyle(document.body).backgroundColor
  return color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent' ? color : '#fff'
}

/**
 * Answers an agent's capture request with a picture of what this tab shows. A
 * tab in the background answers that it is not visible, so another tab of the
 * same session can answer instead.
 */
export const captureUserViewForAgent = async (
  requestId: string,
  settleMs: number,
  interfaceId?: string
): Promise<AgentCaptureStatus> => {
  if (document.visibilityState !== 'visible') {
    await aiApi.uploadUserViewCapture({ requestId, status: 'not_visible' })
    return 'not_visible'
  }
  try {
    const root = document.querySelector(`.${AGENT_CAPTURE_ROOT_CLASS}`)
    const { canvas, frames } = await captureViewportSnapshot(pageBackground(), undefined, {
      ackTimeoutMs: IFRAME_ACK_TIMEOUT_MS,
      settleMs,
      scope: root ?? undefined
    })
    const image = fitWithin(root ? cropToElement(canvas, root) : canvas, MAX_EDGE_PX)
    await aiApi.uploadUserViewCapture({
      requestId,
      status: 'ready',
      image: await toPng(image),
      details: {
        pathname: window.location.pathname,
        interface_id: interfaceId,
        settled: frames.settled,
        frames_total: frames.total,
        frames_captured: frames.captured
      }
    })
    return 'ready'
  } catch (error) {
    console.error('Could not capture the view for the agent:', error)
    await aiApi.uploadUserViewCapture({ requestId, status: 'failed' }).catch(() => undefined)
    return 'failed'
  }
}
